import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { Modal } from '../../ui/Modal';
import { Input } from '../../ui/Input';
import { Button } from '../../ui/Button';

interface CreateOrgModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateOrgModal: React.FC<CreateOrgModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { createOrganization } = useAuth();
  const { showToast } = useToast();

  const [newOrgName, setNewOrgName] = useState('');
  const [isCreatingOrg, setIsCreatingOrg] = useState(false);

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOrgName.trim()) return;

    setIsCreatingOrg(true);
    try {
      const res = await createOrganization(newOrgName.trim());
      showToast({
        type: 'success',
        title: 'Organization Created',
        message: `Organization "${res.organization.name}" is now active.`,
      });
      setNewOrgName('');
      onClose();
      navigate(`/organization/${res.organization.id}`);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to Create Organization',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsCreatingOrg(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create Organization" maxWidth="sm">
      <form onSubmit={handleCreateOrg} className="space-y-4">
        <p className="text-xs text-cf-textSecondary">
          Organizations are containers for your projects, tables, and team members.
        </p>

        <Input
          label="Organization Name"
          type="text"
          size="xs"
          required
          value={newOrgName}
          onChange={(e) => setNewOrgName(e.target.value)}
          placeholder="e.g. Acme Corp"
        />

        <div className="flex justify-end gap-2 pt-2 border-t border-cf-border">
          <Button variant="secondary" size="xs" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="xs"
            type="submit"
            isLoading={isCreatingOrg}
            disabled={isCreatingOrg || !newOrgName.trim()}
            leftIcon={<Plus size={13} />}
          >
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
};
